/**
 * Hooks for Roof Measurement integration via the proxy API.
 *
 * All requests go through Django at /api/integrations/roof-measurement/*
 * which proxies to the standalone roof-measurement-service.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import type {
  RoofMeasurementCoverageResponse,
  RoofMeasurementDetectResponse,
  RoofMeasurementMeasurementResponse,
  RoofMeasurementReportResponse,
  RoofMeasurementReportStatusResponse,
} from '../../types';

const BASE = '/api/integrations/roof-measurement';

// ---- Queries ----

export function useRoofMeasurementCoverage(lat?: number, lon?: number) {
  return useQuery({
    queryKey: ['roof-measurement-coverage', lat, lon],
    queryFn: async () => {
      const response = await api.get<RoofMeasurementCoverageResponse>(
        `${BASE}/coverage/?lat=${lat}&lon=${lon}`
      );
      return response.data;
    },
    enabled: lat != null && lon != null,
    staleTime: 1000 * 60 * 60, // 1h — coverage rarely changes
  });
}

export function useRoofMeasurementReportStatus(reportId?: string) {
  return useQuery({
    queryKey: ['roof-measurement-report-status', reportId],
    queryFn: async () => {
      const response = await api.get<RoofMeasurementReportStatusResponse>(
        `${BASE}/reports/${reportId}/status/`
      );
      return response.data;
    },
    enabled: !!reportId,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status === 'completed' || status === 'failed') return false;
      return 3000; // Poll every 3s while pending/processing
    },
  });
}

// ---- Mutations ----

export function useRoofMeasurementDetect() {
  return useMutation({
    mutationFn: async (params: { lat: number; lon: number; aoi_size_m?: number }) => {
      const response = await api.post<RoofMeasurementDetectResponse>(
        `${BASE}/detect/`,
        params
      );
      return response.data;
    },
  });
}

export function useRoofMeasurementMeasurements() {
  return useMutation({
    mutationFn: async (params: {
      polygon: Record<string, unknown>;
      survey_id?: string;
    }) => {
      const response = await api.post<RoofMeasurementMeasurementResponse>(
        `${BASE}/measurements/`,
        params
      );
      return response.data;
    },
  });
}

export function useRoofMeasurementFullMeasurement() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: { lat: number; lon: number }) => {
      const response = await api.post<RoofMeasurementMeasurementResponse>(
        `${BASE}/measurements/full/`,
        params
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['measurements'] });
    },
  });
}

export function useRoofMeasurementGenerateReport() {
  return useMutation({
    mutationFn: async (params: {
      address: string;
      lat: number;
      lon: number;
      polygon?: Record<string, unknown>;
      survey_id?: string;
    }) => {
      const response = await api.post<RoofMeasurementReportResponse>(
        `${BASE}/reports/generate/`,
        params
      );
      return response.data;
    },
  });
}

export function useRoofMeasurementReportDownloadUrl(reportId?: string) {
  if (!reportId) return null;
  return `${BASE}/reports/${reportId}/download/`;
}
